import React from "react";
import AppButton from "../layout/AppButton";

/**
 * Modal for assigning tool pages (and additional features) to a tool role.
 *
 * Props:
 *   open            - boolean
 *   roleName        - name shown in the header
 *   pages           - full pages/features catalog [{ key, label, type? }] — type is
 *                     "page" (checkbox, default) or "feature" (toggle switch)
 *   loading         - boolean — shows loading state in list
 *   selectedKeys    - array of currently selected page keys
 *   featureToggles  - { [featureLabel]: boolean } — disabled by default unless explicitly true
 *   onToggle        - (pageKey) => void
 *   onToggleFeature - (featureLabel) => void
 *   onSelectAll     - select every page (features are toggled individually, not bulk)
 *   onClearAll      - clear all page selections
 *   onSubmit        - form submit handler
 *   onClose         - close handler
 *   submitting      - boolean — disables actions while saving
 */
const AssignPagesModal = ({
  open,
  roleName,
  pages = [],
  loading = false,
  selectedKeys = [],
  featureToggles = {},
  onToggle,
  onToggleFeature,
  onSelectAll,
  onClearAll,
  onSubmit,
  onClose,
  submitting = false,
}) => {
  if (!open) return null;

  const pageItems = pages.filter((p) => (p.type || "page") === "page");
  const featureItems = pages.filter((p) => p.type === "feature");
  const enabledFeatureCount = featureItems.filter((f) => !!featureToggles[f.label]).length;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content rr-manageCard">
            <div className="modal-header border-0 pb-1">
              <h5 className="modal-title">Assign Pages — {roleName}</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>

            <form onSubmit={onSubmit}>
              <div className="modal-body">
                <p className="text-muted mb-2">Select which pages and features this tool role can access.</p>

                <div className="rr-mapToolbar mb-3">
                  <span className="rr-mapCount">Selected: {selectedKeys.length + enabledFeatureCount}</span>
                  <div className="rr-mapActions">
                    <AppButton type="button" variant="outline" onClick={onSelectAll}>Select All Pages</AppButton>
                    <AppButton type="button" variant="secondary" onClick={onClearAll}>Clear Pages</AppButton>
                  </div>
                </div>

                {loading ? (
                  <div className="text-muted py-2">Loading...</div>
                ) : (
                  <>
                    <div className="rr-mapReportList">
                      {pageItems.length === 0 ? (
                        <div className="text-muted py-2">No pages available.</div>
                      ) : (
                        pageItems.map((page) => (
                          <label key={page.key} className="rr-mapReportItem">
                            <input
                              type="checkbox"
                              checked={selectedKeys.includes(page.key)}
                              onChange={() => onToggle(page.key)}
                            />
                            <span>{page.label}</span>
                          </label>
                        ))
                      )}
                    </div>

                    {featureItems.length > 0 && (
                      <>
                        <p className="text-muted mt-3 mb-2" style={{ fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                          Additional Features
                        </p>
                        <div className="rr-mapReportList">
                          {featureItems.map((feature) => {
                            const enabled = !!featureToggles[feature.label];
                            return (
                              <label
                                key={feature.key}
                                className="rr-mapReportItem"
                                style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                              >
                                <span>{feature.label}</span>
                                <span
                                  role="switch"
                                  aria-checked={enabled}
                                  onClick={() => onToggleFeature(feature.label)}
                                  style={{
                                    position: "relative",
                                    width: "38px",
                                    height: "20px",
                                    borderRadius: "9999px",
                                    cursor: "pointer",
                                    flexShrink: 0,
                                    background: enabled ? "var(--app-button-color, #bb1ccc)" : "#d1d5db",
                                    transition: "background 0.2s",
                                  }}
                                >
                                  <span
                                    style={{
                                      position: "absolute",
                                      top: "2px",
                                      left: enabled ? "20px" : "2px",
                                      width: "16px",
                                      height: "16px",
                                      borderRadius: "50%",
                                      background: "#fff",
                                      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                                      transition: "left 0.2s",
                                    }}
                                  />
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              <div className="modal-footer border-0 justify-content-end d-flex gap-2">
                <AppButton type="submit" variant="fill" disabled={submitting}>
                  {submitting ? "Saving..." : "Save Pages"}
                </AppButton>
                <AppButton type="button" variant="secondary" onClick={onClose} disabled={submitting}>Cancel</AppButton>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
};

export default AssignPagesModal;
